import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { authEndPoints } from '../Services/AllApi';
import { apiConnector } from '../Services/ApiConnector';
import { FaSpinner, FaEye, FaEyeSlash, FaPhone, FaEnvelope, FaLock, FaUser } from 'react-icons/fa';
import toast from 'react-hot-toast';
//import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
//import { setToken, setUser } from '../Slices/AuthSlice';
import { setToken } from '../Slices/AuthSlice';
import usePageTitle from '../hooks/usePageTitle';

const Auth = () => {
  usePageTitle('Login & Signup');
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  //const location = useLocation();
  const dispatch = useDispatch();

  const { SIGNUP_API, LOGIN_API } = authEndPoints;

  // Helper function to get user-friendly error messages for HTTP status codes
  const getStatusMessage = (status) => {
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Invalid credentials. Please check your phone number and password.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Service not found. Please try again later.';
      case 409:
        return 'Account already exists with this phone number. Please try logging in instead.';
      case 422:
        return 'Invalid data provided. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${status}. Please try again.`;
    }
  };

  const {
    register,
    handleSubmit,
    //watch,
    formState: { errors },
    reset
  } = useForm();

  //const password = watch('password');

  // Function to handle redirect after successful auth
  const handleRedirect = () => {
    // Always redirect to home page after successful login
    console.log('Redirecting to home page');
    navigate('/', { replace: true });
  };

  // Phone number validation based on backend rules
  const validatePhone = (value) => {
    if (!value || value.trim() === '') {
      return 'Phone number is required';
    }

    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');
    
    // Handle different prefixes
    if (digits.startsWith('91') && digits.length === 12) {
      // Remove +91 or 91 prefix
      digits = digits.substring(2);
    } else if (digits.startsWith('0') && digits.length === 11) {
      // Remove leading 0
      digits = digits.substring(1);
    }

    // Check if exactly 10 digits after formatting
    if (digits.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }

    // Check if starts with 6, 7, 8, or 9
    const firstDigit = digits.charAt(0);
    if (!['6', '7', '8', '9'].includes(firstDigit)) {
      return 'Phone number must start with 6, 7, 8, or 9';
    }

    return true;
  };

  // Password strength validation
  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 3) return 'Password must be at least 8 characters long';
   
    return true;
  };

  // Name validation (optional)
  const validateName = (value) => {
    if (!value) return true; // Name is optional
    if (value.trim().length < 2) return 'Name must be at least 2 characters long';
    if (value.trim().length > 50) return 'Name must be less than 50 characters';
    return true;
  };

  // Email validation (optional)
  const validateEmail = (value) => {
    if (!value) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return true;
  };


  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      if (activeTab === 'signup') {
        // Signup API call
        const response = await apiConnector('POST', SIGNUP_API, {
          name: data.name,
          phone: data.phone,
          email: data.email,
          password: data.password
        });

        console.log('Signup response:', response);
        
        if (response && response.data) {
          // Save access_token to localStorage if available (user gets token on signup)
          if (response.data.access_token) {
            localStorage.setItem('token', response.data.access_token);
            // Update Redux state
            dispatch(setToken(response.data.access_token));
            // Save token type and expiry info
            if (response.data.token_type) {
              localStorage.setItem('token_type', response.data.token_type);
            }
            if (response.data.expires_in) {
              localStorage.setItem('expires_in', response.data.expires_in);
            }
            // Save user ID
            if (response.data.id) {
              localStorage.setItem('user_id', response.data.id);
              console.log('User ID saved:', response.data.id);
            }
            toast.success('Account created successfully! Welcome!');
            handleRedirect(); // Redirect immediately since user has token
          } else {
            // Fallback: if no token in signup response, ask user to login
            toast.success('Account created successfully! Please login.');
            setActiveTab('login');
            reset();
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } else {
        // Login API call
        const response = await apiConnector('POST', LOGIN_API, {
          phone: data.phone,
          password: data.password
        });

        console.log('Login response:', response);
        
        if (response && response.data && response.data.access_token) {
          // Save access_token to localStorage
          localStorage.setItem('token', response.data.access_token);
          // Update Redux state
          dispatch(setToken(response.data.access_token));
          // Save token type and expiry info
          if (response.data.token_type) {
            localStorage.setItem('token_type', response.data.token_type);
          }
          if (response.data.expires_in) {
            localStorage.setItem('expires_in', response.data.expires_in);
          }
          toast.success('Login successful!');
          handleRedirect(); // Use smart redirect function
        } else {
          throw new Error('Invalid response format or missing access_token');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different types of API errors with more specific messages
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // First, try to extract the actual error message from the response
        let errorMessage = null;
        
        // Check if the response data is a string (direct error message)
        if (typeof errorData === 'string') {
          errorMessage = errorData.trim();
        }
        // Check for specific error message fields
        else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (errorData.errors) {
          // Handle validation errors array
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join(', ');
          } else {
            errorMessage = JSON.stringify(errorData.errors);
          }
        } else if (typeof errorData === 'object' && errorData !== null) {
          // If it's an object, try to find any string value that looks like an error message
          const stringValues = Object.values(errorData).filter(val => typeof val === 'string' && val.trim().length > 0);
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          }
        }
        
        // Display the error message if we found one
        if (errorMessage) {
          toast.error(errorMessage);
        } else {
          // If we have response data but no clear message, show the status
          const statusMessage = getStatusMessage(error.response.status);
          toast.error(statusMessage);
        }
      } else if (error.response?.status) {
        // Handle HTTP status codes without specific error data
        const statusMessage = getStatusMessage(error.response.status);
        toast.error(statusMessage);
      } else if (error.message) {
        // Handle network errors or other issues
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          toast.error('Network error. Please check your internet connection and try again.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error(activeTab === 'signup' ? 'Signup failed. Please try again.' : 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    reset();
    setShowPassword(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 mt-16">
          <h1 className="text-3xl font-bold text-orange-800 mb-2">ANANT ARADHYA</h1>
          <p className="text-gray-600">Welcome to your spiritual journey</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Tabs */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => switchTab('login')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                activeTab === 'login'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => switchTab('signup')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                activeTab === 'signup'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Signup
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaPhone className="inline mr-2" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                {...register('phone', { validate: validatePhone })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your phone number (e.g., 91 9876543210)"
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
              )}
            </div>

            {/* Name (Signup only) */}
            {activeTab === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaUser className="inline mr-2" />
                  Full Name (Optional)
                </label>
                <input
                  type="text"
                  {...register('name', { validate: validateName })}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
            )}

            {/* Email (Signup only) */}
            {activeTab === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address (Optional)
                </label>
                <input
                  type="email"
                  {...register('email', { validate: validateEmail })}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaLock className="inline mr-2" />
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { validate: validatePassword })}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-10 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={activeTab === 'signup' ? 'Create your password' : 'Enter your password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>


            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  {activeTab === 'signup' ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                activeTab === 'signup' ? 'Create Account' : 'Sign In'
              )}
            </button>
          </form>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            {activeTab === 'login' ? (
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => switchTab('signup')}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                >
                  Sign up here
                </button>
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => switchTab('login')}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                >
                  Sign in here
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
